import i18n from '@atpoint/atpjs/helpers/i18n'
import { Loader } from '@googlemaps/js-api-loader'
import { Box } from '@material-ui/core'
import { List, fromJS, Map as immutableMap } from 'immutable'
import React, { useCallback } from 'react'
import { H6, Link, P } from 'swisscard/components/typography'
import config from 'swisscard/config'
import { getEnv } from 'swisscard/helpers/env'
import Autocomplete from '../autocomplete'

import {
  BASE_MAP_OPTIONS,
  DEFAULT_COUNTRY,
  NEARBY_SEARCH_RADIUS,
  PLACE_FIELDS,
  PLACE_ZOOM,
  RESULT_PLACE_TYPES_BLACKLIST,
} from './config'

import {
  attemptToStartAtNavigatorGeoLocation,
  buildCrmResult,
  computeNameAndOrAddress,
  logError,
  mapPredictionsToAutocompleteResults,
} from './helpers'

import { businessPlaceInputStyles } from './business_place_input_styles'

import type { InputPropsType } from '@atpoint/atpjs/components/form/input'
import type { NearbyResultType, ResultType } from './helpers'

const t = i18n.prefix(['shared', 'components', 'form', 'businessPlaceInput'])

const googleMapsLoader = new Loader({
  apiKey: getEnv('GOOGLE_MAPS_API_KEY') || '',
  libraries: ['places'],
})

export const BusinessPlaceInput = (props: InputPropsType) => {
  // TODO: must have a distinct element id/index, for allowing multiple instances
  const mapElementId = 'business-place-input-map'
  const classes = businessPlaceInputStyles()

  const [autocompleteService, setAutocompleteService] = React.useState<
    google.maps.places.AutocompleteService | undefined
  >(undefined)

  const [map, setMap] = React.useState<google.maps.Map | undefined>(undefined)
  const [marker] = React.useState<google.maps.Marker | undefined>(undefined)
  const markerRef = React.useRef(marker)

  const [nearbyResults, setNearbyResults] = React.useState<NearbyResultType[]>([])
  const [displayMoreNearbyResults, setDisplayMoreNearbyResults] = React.useState(false)

  // markerResult is used for store info for the current marker position's result
  const [markerResult, setMarkerResult] = React.useState<ResultType | undefined>(undefined)

  // result is used for store info for displaying the actual chosen result
  const [result, setResult] = React.useState<ResultType | undefined>(undefined)

  // Errors
  const [mapErrorMessage, setMapErrorMessage] = React.useState<string | undefined>(undefined)
  const [placesServiceStatuses, setPlacesServiceStatuses] = React.useState<string[]>([])
  const [geocoderStatuses, setGeocoderStatuses] = React.useState<string[]>([])

  const mapListener = useCallback(
    (map: google.maps.Map, placesService: google.maps.places.PlacesService) =>
      (mapMouseEvent: google.maps.MapMouseEvent) => {
        mapMouseEvent.stop() // Prevent the event from showing the infoWindow on the clicked location.
        setMapErrorMessage('')
        setResult(undefined)
        setMarkerResult(undefined)

        const geocoder = new google.maps.Geocoder()

        geocoder.geocode(
          { location: mapMouseEvent.latLng },
          (geocoderResults: google.maps.GeocoderResult[], status: google.maps.GeocoderStatus) => {
            if (geocoderResults && status !== google.maps.GeocoderStatus.OK) {
              logError('google.maps.GeocoderStatus', status, geocoderStatuses)
              return
            }

            if (geocoderResults?.[0]) {
              // Set a marker on the clicked location
              const geocoderResult = geocoderResults[0]
              const location = geocoderResult.geometry.location
              map.setCenter(location)
              map.setZoom(PLACE_ZOOM)

              // Erase any previous marker from the map
              if (markerRef.current) {
                markerRef.current.setMap(null)
              }

              markerRef.current = new google.maps.Marker({ map: map, position: location })

              if (placesService) {
                // Get details for the clicked location
                placesService.getDetails(
                  { placeId: geocoderResult.place_id, fields: PLACE_FIELDS },
                  (placeResult: google.maps.places.PlaceResult, status) => {
                    if (!placeResult || status !== google.maps.places.PlacesServiceStatus.OK) {
                      logError('google.maps.places.PlacesServiceStatus', status, placesServiceStatuses)
                    } else {
                      setMarkerResult({
                        name: placeResult?.name,
                        address: geocoderResult?.formatted_address,
                        placeId: geocoderResult?.place_id,
                      })
                    }
                  },
                )

                // Retrieve nearby PlaceResult items for the clicked location
                placesService.nearbySearch(
                  {
                    location: location,
                    radius: NEARBY_SEARCH_RADIUS,
                  },
                  (results: google.maps.places.PlaceResult[], status: string) => {
                    if (status !== google.maps.places.PlacesServiceStatus.OK) {
                      logError('google.maps.places.PlacesServiceStatus', status, placesServiceStatuses)
                    } else {
                      // Filter blacklisted place types
                      const filtered = results.filter((placeResult: google.maps.places.PlaceResult) =>
                        RESULT_PLACE_TYPES_BLACKLIST.every(
                          (blackListedType: string) => !placeResult.types?.includes(blackListedType),
                        ),
                      )

                      const nearbyResults = filtered.map((placeResult: google.maps.places.PlaceResult) => {
                        return {
                          name: placeResult.name,
                          placeId: placeResult.place_id,
                          vicinity: placeResult.vicinity,
                        }
                      })

                      setNearbyResults(nearbyResults)
                    }
                  },
                )
              }
            }
          },
        )
      },
    [geocoderStatuses, placesServiceStatuses],
  )

  React.useEffect(() => {
    googleMapsLoader.loadCallback((e) => {
      if (e) {
        alert('GoogleMapsLoader error')
      } else {
        const map = new google.maps.Map(document.getElementById(mapElementId) as HTMLElement, BASE_MAP_OPTIONS)
        const placesService = new google.maps.places.PlacesService(map)
        map.addListener('click', mapListener(map, placesService))

        setAutocompleteService(new google.maps.places.AutocompleteService())
        setMap(map)
        setPlacesServiceStatuses([
          google.maps.places.PlacesServiceStatus.INVALID_REQUEST,
          google.maps.places.PlacesServiceStatus.OVER_QUERY_LIMIT,
          google.maps.places.PlacesServiceStatus.REQUEST_DENIED,
          google.maps.places.PlacesServiceStatus.UNKNOWN_ERROR,
        ])
        setGeocoderStatuses([
          google.maps.GeocoderStatus.INVALID_REQUEST,
          google.maps.GeocoderStatus.OVER_QUERY_LIMIT,
          google.maps.GeocoderStatus.REQUEST_DENIED,
          google.maps.GeocoderStatus.UNKNOWN_ERROR,
        ])
        attemptToStartAtNavigatorGeoLocation(map, PLACE_ZOOM)
      }
    })
  }, [mapListener])

  const getResults = React.useCallback(
    (value: string) => {
      return new Promise((resolve, reject) => {
        if (autocompleteService && value) {
          autocompleteService.getPlacePredictions(
            {
              input: value,
              componentRestrictions: { country: DEFAULT_COUNTRY },
            },
            (
              predictions: google.maps.places.QueryAutocompletePrediction[] | null,
              status: google.maps.places.PlacesServiceStatus,
            ) => {
              if (!predictions || status !== google.maps.places.PlacesServiceStatus.OK) {
                logError('google.maps.places.PlacesServiceStatus', status, placesServiceStatuses)
                reject([])
              }

              resolve(mapPredictionsToAutocompleteResults(predictions))
            },
          )
        } else reject([])
      })
    },
    [autocompleteService, placesServiceStatuses],
  )

  const placeMarkerFromPlaceId = React.useCallback(
    (map: google.maps.Map | undefined, props: InputPropsType, placeId?: string) => {
      const geocoder = new google.maps.Geocoder()
      setMapErrorMessage('')
      geocoder.geocode({ placeId: placeId }, (geocoderResults, status) => {
        if (geocoderResults && status !== google.maps.GeocoderStatus.OK) {
          return
        }

        if (map && geocoderResults?.[0]) {
          const geocoderResult = geocoderResults[0]
          const location = geocoderResult.geometry.location
          map.setCenter(location)
          map.setZoom(PLACE_ZOOM)

          if (
            RESULT_PLACE_TYPES_BLACKLIST.some((blackListedPlaceType: string) =>
              geocoderResult.types.includes(blackListedPlaceType),
            )
          ) {
            setMapErrorMessage(
              'geocoderResult has a type part of RESULT_PLACE_TYPES_BLACKLIST, ignoring value assignment.',
            )
            return
          }

          if (markerRef.current) {
            markerRef.current.setMap(null)
          }

          markerRef.current = new google.maps.Marker({
            map: map,
            place: { placeId, location },
          })

          setNearbyResults([]) // Clear any nearby results we may have produced by manually clicking on the map

          buildCrmResult(map, geocoderResult, placeId || '')
            .then((crmResult) => {
              const value = fromJS(crmResult)
              setResult({ name: crmResult.accountName, address: geocoderResult.formatted_address })
              if (props.onChange) {
                props.onChange(value)
              }
            })
            .catch(() => {
              setMapErrorMessage('Unable to compose a result for the CRM')
            })
        }
      })
    },
    [],
  )

  const autocompleteValue = result?.address
  const displayMarkerSuggestion = !!markerResult && !result
  const displayNearbyResults = nearbyResults && nearbyResults.length > 0
  const nearbyResultsSubset = displayMoreNearbyResults ? nearbyResults : nearbyResults.slice(0, 3)

  const computeErrorMessage = (): string | boolean => {
    const { errorMessage } = props
    if (typeof errorMessage === 'string') {
      return errorMessage
    }
    if (immutableMap.isMap(errorMessage)) {
      return List(errorMessage.values()).filter(Boolean).first() as string
    }
    return false
  }

  return (
    <>
      <Autocomplete
        {...props}
        debounceTime={config.AUTOCOMPLETE_DEBOUNCE_TIME}
        errorMessage={computeErrorMessage()}
        minSearchLength={2}
        noResultsMessage={t(['noResultsMessage'])}
        value={autocompleteValue}
        // @ts-ignore
        getResults={(value: string) => getResults(value)}
        onChange={(placeId: string) => placeMarkerFromPlaceId(map, props, placeId)}
      />

      <P variant="info">{t(['orClickOnMap'])}</P>

      <div style={{ height: '300px', marginBottom: '20px', width: '100%' }} id={mapElementId} />

      {(displayMarkerSuggestion || displayNearbyResults) && (
        <Box mb={1}>
          <P variant="info">{t(['choose'])}</P>
        </Box>
      )}

      {displayMarkerSuggestion && (
        <Box className={classes.box}>
          <H6>{t(['currentMarkerSelection'])}</H6>
          <Link onClick={() => placeMarkerFromPlaceId(map, props, markerResult?.placeId)}>
            {markerResult && computeNameAndOrAddress(markerResult.name, markerResult.address)}
          </Link>
        </Box>
      )}

      {displayNearbyResults && (
        <>
          <Box className={classes.box}>
            <H6>{t(['nearby'])}</H6>

            {nearbyResultsSubset.length > 0 && (
              <ul className={classes.nearByList}>
                {nearbyResultsSubset.map(({ name, placeId, vicinity }: NearbyResultType) => (
                  <li key={placeId}>
                    <Link onClick={() => placeMarkerFromPlaceId(map, props, placeId)}>
                      {[name, vicinity].filter(Boolean).join(', ')}
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </Box>

          {!displayMoreNearbyResults && (
            <Box mb={1} px={2} py={1}>
              <Link size="small" onClick={() => setDisplayMoreNearbyResults(true)}>
                {t(['moreNearbyResults'])}
              </Link>
            </Box>
          )}
        </>
      )}

      {false && mapErrorMessage && (
        <P size="small" variant="error">
          {mapErrorMessage}
        </P>
      )}

      {result && (
        <Box className={classes.box}>
          <ChosenBusinessPlace name={result.name} address={result.address} />
        </Box>
      )}
    </>
  )
}

type ChosenBusinessPlacePropsType = {
  name?: string
  address?: string
}

const ChosenBusinessPlace: React.FC<ChosenBusinessPlacePropsType> = (props: ChosenBusinessPlacePropsType) => {
  const { name, address } = props

  return (
    <>
      <P variant="info">{t(['chosen'])}</P>
      <H6 noMargin>
        {name && address?.includes(name) ? (
          address
        ) : (
          <>
            {name}
            <br />
            {address}
          </>
        )}
      </H6>
    </>
  )
}
